import {Button as MuiButton, ButtonProps} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import React, {ReactNode} from "react";
import {Colors} from "../colors";
import {getButtonRadius} from "../dynamic-ui";

export type ButtonTypes =
  | "contained-primary"
  | "contained-secondary"
  | "contained-error"
  | "outlined-primary"
  | "outlined-secondary"
  | "text"
  | "contained-partner";

export type ButtonType = ButtonProps & {
  buttonType?: ButtonTypes;
  variant?: "contained" | "text" | "outlined";
  color?: "secondary" | "inherit" | "primary" | "success" | "error" | "info" | "warning" | undefined;
  onClick?: React.MouseEventHandler;
  longWidth?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  type?: string;
};

const getVariantAndColor = (
  type: ButtonTypes | undefined
): {variant: ButtonProps["variant"]; color: ButtonProps["color"]} => {
  if (type) {
    if (type === "contained-primary") {
      return {variant: "contained", color: "primary"};
    }
    if (type === "contained-secondary") {
      return {variant: "contained", color: "secondary"};
    }
    if (type === "contained-error") {
      return {variant: "contained", color: "error"};
    }
    if (type === "outlined-primary") {
      return {variant: "outlined", color: "primary"};
    }
    if (type === "outlined-secondary") {
      return {variant: "outlined", color: "info"};
    }
    if (type === "contained-partner") {
      return {variant: "contained", color: "info"};
    }
    if (type === "text") {
      return {variant: "text", color: undefined};
    }
  }
  return {variant: "contained", color: "primary"};
};

const buttonProps = {
  height: {
    small: 36,
    medium: 44,
    large: 48,
  },
  padding: {
    small: "10px 14px",
    medium: "10px 18px",
    large: "12px 20px",
  },
  font: {
    small: "14px",
    medium: "16px",
    large: "16px",
  },
};

export const Button = ({
  size = "large",
  buttonType = "contained-primary",
  onClick,
  className = "Button",
  icon,
  longWidth = false,
  children,
  fullWidth = false,
  type = undefined,
  sx,
  ...props
}: ButtonType) => {
  const theme = useTheme();
  const {variant, color} = getVariantAndColor(buttonType);
  return (
    <MuiButton
      className={className}
      variant={variant}
      size={size}
      color={color}
      type={type}
      onClick={onClick}
      fullWidth={fullWidth}
      disableRipple={buttonType === "text"}
      sx={{
        margin: "0px",
        alignItem: "center",
        justifyContent: "center",
        textTransform: "none",
        fontSize: buttonProps.font[size],
        fontWeight: 600,
        borderRadius: getButtonRadius(),
        gap: "8px",
        height: buttonProps.height[size],
        padding: children ? buttonProps.padding[size] : "0px",
        minWidth: children ? buttonProps.height[size] * 2 : "38px",
        whiteSpace: "nowrap",
        ...(!children && {
          "& .MuiButton-startIcon": {
            margin: "0px",
          },
        }),
        ...(longWidth && {width: "300px"}),
        ...(buttonType === "contained-secondary" && {
          color: theme.palette.primary.main,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        }),
        ...(buttonType === "contained-partner" && {backgroundColor: Colors.secondary._800}),
        ...(buttonType === "text" && {
          "&:hover, &:focus, &:active": {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }),
        ...sx,
      }}
      {...props}
    >
      {icon}
      {children}
    </MuiButton>
  );
};
